(() => {
  const SELECTOR = '[data-mr-global-nav]';
  const DESKTOP = window.matchMedia('(min-width: 1025px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const ACCORDION_DURATION = 280;

  function init(root) {
    if (root.dataset.mrNavReady === 'true') return;
    root.dataset.mrNavReady = 'true';

    const drawer = root.querySelector('[data-mr-drawer]');
    const mobileToggle = root.querySelector('[data-mr-mobile-toggle]');
    const mobileClose = root.querySelector('[data-mr-mobile-close]');
    const topDropdowns = [...root.querySelectorAll('[data-mr-top-dropdown]')];
    const groupToggles = [...root.querySelectorAll('[data-mr-group-toggle]')];
    const featureToggles = [...root.querySelectorAll('[data-mr-feature-toggle]')];

    let previousBodyOverflow = '';

    const isDesktop = () => DESKTOP.matches;
    const collapseEmphasized = root.dataset.mrCollapseEmphasized === 'true';
    const viewAllLabel = root.dataset.mrViewAllLabel || 'View all';

    function setPanelTop() {
      if (!isDesktop()) return;

      const openItem = root.querySelector('[data-mr-top-dropdown].is-open');
      if (!openItem) return;

      const openPanel = openItem.querySelector('[data-mr-top-panel]');
      if (!openPanel) return;

      const rootRect = root.getBoundingClientRect();
      const top = Math.max(0, Math.round(rootRect.bottom));

      root.style.setProperty('--mr-nav-panel-top', `${top}px`);
    }

    function clearAnimationHandler(body) {
      if (!body) return;

      if (body._mrHeightHandler) {
        body.removeEventListener('transitionend', body._mrHeightHandler);
        body._mrHeightHandler = null;
      }

      if (body._mrHeightTimer) {
        window.clearTimeout(body._mrHeightTimer);
        body._mrHeightTimer = null;
      }
    }

    function clearAnimatedStyles(body, { keepHeight = false } = {}) {
      if (!body) return;

      clearAnimationHandler(body);

      body.style.removeProperty('display');
      body.style.removeProperty('overflow');
      body.style.removeProperty('opacity');
      body.style.removeProperty('transition');

      if (!keepHeight) {
        body.style.removeProperty('height');
      }
    }

    function completeOnHeightTransition(body, callback) {
      clearAnimationHandler(body);

      let completed = false;

      const finish = () => {
        if (completed) return;
        completed = true;

        clearAnimationHandler(body);
        callback();
      };

      const handler = (event) => {
        if (event.target !== body || event.propertyName !== 'height') return;
        finish();
      };

      body._mrHeightHandler = handler;
      body.addEventListener('transitionend', handler);

      // Fallback for interrupted transitions / browsers that skip transitionend.
      body._mrHeightTimer = window.setTimeout(
        finish,
        ACCORDION_DURATION + 80
      );
    }

    function animateMobileHeight(
      body,
      shouldOpen,
      {
        animate = true,
        beforeOpen = null,
        beforeClose = null,
        afterOpen = null,
        afterClose = null
      } = {}
    ) {
      if (!body) return;

      clearAnimationHandler(body);

      const useAnimation =
        animate &&
        !isDesktop() &&
        !REDUCED_MOTION.matches;

      /* Desktop is not height-controlled by the mobile accordion system. */
      if (isDesktop()) {
        if (shouldOpen) {
          beforeOpen?.();
        } else {
          beforeClose?.();
          afterClose?.();
        }

        clearAnimatedStyles(body);
        afterOpen?.();
        return;
      }

      /* Instant mobile state change, used when closing the drawer/resetting. */
      if (!useAnimation) {
        if (shouldOpen) {
          beforeOpen?.();

          body.style.display = 'block';
          body.style.height = 'auto';
          body.style.overflow = '';
          body.style.opacity = '';
          body.style.transition = '';

          afterOpen?.();
        } else {
          beforeClose?.();
          afterClose?.();

          body.style.display = '';
          body.style.height = '0px';
          body.style.overflow = '';
          body.style.opacity = '';
          body.style.transition = '';
        }

        return;
      }

      if (shouldOpen) {
        beforeOpen?.();

        // Parent state may control display:none/block. Force it measurable.
        body.style.display = 'block';
        body.style.overflow = 'hidden';
        body.style.height = '0px';
        body.style.opacity = '0';
        body.style.transition =
          'height 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 180ms ease';

        // Commit the closed state before measuring/opening.
        body.offsetHeight;

        const targetHeight = body.scrollHeight;

        window.requestAnimationFrame(() => {
          body.style.height = `${targetHeight}px`;
          body.style.opacity = '1';
        });

        completeOnHeightTransition(body, () => {
          body.style.height = 'auto';
          body.style.display = '';
          body.style.overflow = '';
          body.style.opacity = '';
          body.style.transition = '';

          afterOpen?.();
        });

        return;
      }

      // CLOSE
      const currentHeight = Math.max(
        body.getBoundingClientRect().height,
        body.scrollHeight
      );

      body.style.display = 'block';
      body.style.overflow = 'hidden';
      body.style.height = `${currentHeight}px`;
      body.style.opacity = '1';
      body.style.transition =
        'height 280ms cubic-bezier(0.4, 0, 0.2, 1), opacity 180ms ease';

      body.offsetHeight;

      beforeClose?.();

      window.requestAnimationFrame(() => {
        body.style.height = '0px';
        body.style.opacity = '0';
      });

      completeOnHeightTransition(body, () => {
        afterClose?.();

        body.style.display = '';
        body.style.height = '0px';
        body.style.overflow = '';
        body.style.opacity = '';
        body.style.transition = '';
      });
    }

    /* =========================================================
       TOP-LEVEL DROPDOWNS
       Tools / Promotions / Support
    ========================================================= */

    function closeTopDropdown(item, animate = true) {
      if (!item) return;

      const toggle = item.querySelector('[data-mr-top-toggle]');
      const panel = item.querySelector('[data-mr-top-panel]');

      if (!panel) return;

      if (isDesktop()) {
        item.classList.remove('is-open');
        toggle?.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
        clearAnimatedStyles(panel);
        return;
      }

      animateMobileHeight(panel, false, {
        animate,
        beforeClose() {
          item.classList.remove('is-open');
          toggle?.setAttribute('aria-expanded', 'false');
          panel.setAttribute('aria-hidden', 'true');
        }
      });
    }

    function closeTopDropdowns(except = null, animate = true) {
      topDropdowns.forEach((item) => {
        if (item === except) return;
        if (!item.classList.contains('is-open')) return;

        closeTopDropdown(item, animate);
      });
    }

    function openTopDropdown(item, animate = true) {
      if (!item) return;

      const toggle = item.querySelector('[data-mr-top-toggle]');
      const panel = item.querySelector('[data-mr-top-panel]');

      if (!toggle || !panel) return;

      closeTopDropdowns(item, animate);

      if (isDesktop()) {
        clearAnimatedStyles(panel);
        item.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        panel.setAttribute('aria-hidden', 'false');
        setPanelTop();
        return;
      }

      animateMobileHeight(panel, true, {
        animate,
        beforeOpen() {
          item.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
          panel.setAttribute('aria-hidden', 'false');
        }
      });
    }
        /* =========================================================
       STANDARD GROUP ACCORDIONS
       Saws / Warranty / Downloads / etc.
    ========================================================= */

    function setGroupOpen(group, shouldOpen, animate = true) {
      if (!group) return;

      const toggle = group.querySelector('[data-mr-group-toggle]');
      const body = group.querySelector(':scope > [data-mr-group-body]');

      if (!body) return;

      if (isDesktop()) {
        clearAnimatedStyles(body);
        group.classList.toggle('is-open', shouldOpen);
        toggle?.setAttribute(
          'aria-expanded',
          shouldOpen ? 'true' : 'false'
        );
        return;
      }

      animateMobileHeight(body, shouldOpen, {
        animate,

        beforeOpen() {
          group.classList.add('is-open');

          toggle?.setAttribute(
            'aria-expanded',
            'true'
          );
        },

        beforeClose() {
          group.classList.remove('is-open');

          toggle?.setAttribute(
            'aria-expanded',
            'false'
          );
        }
      });
    }

    function closeGroups(except = null, animate = true) {
      root
        .querySelectorAll('[data-mr-group].is-open')
        .forEach((group) => {
          if (group === except) return;

          setGroupOpen(
            group,
            false,
            animate
          );
        });
    }

    /* =========================================================
       FEATURED RANGE ACCORDIONS
    ========================================================= */

    function setFeatureOpen(feature, shouldOpen, animate = true) {
      if (!feature) return;

      const toggle =
        feature.querySelector(
          '[data-mr-feature-toggle]'
        );

      const body =
        feature.querySelector(
          '.mr-nav-feature-accordion__body'
        );

      if (!body) return;

      animateMobileHeight(
        body,
        shouldOpen,
        {
          animate,

          beforeOpen() {
            feature.classList.add('is-open');

            toggle?.setAttribute(
              'aria-expanded',
              'true'
            );
          },

          beforeClose() {
            feature.classList.remove('is-open');

            toggle?.setAttribute(
              'aria-expanded',
              'false'
            );
          }
        }
      );
    }

    function closeFeatureAccordions(
      except = null,
      animate = true
    ) {
      root
        .querySelectorAll(
          '[data-mr-feature-accordion].is-open'
        )
        .forEach((feature) => {
          if (feature === except) return;

          setFeatureOpen(
            feature,
            false,
            animate
          );
        });
    }

    /* =========================================================
       EMPHASIZED FAMILY ACCORDIONS
    ========================================================= */

    function setEmphasizedSegmentOpen(
      segment,
      shouldOpen,
      animate = true
    ) {
      if (!segment) return;

      const heading =
        segment.querySelector(
          ':scope > .mr-nav-link--emphasized'
        );

      const body =
        segment.querySelector(
          ':scope > .mr-nav-family__segment-children'
        );

      if (!heading || !body) return;

      if (isDesktop()) {
        clearAnimatedStyles(body);

        segment.classList.toggle(
          'is-open',
          shouldOpen
        );

        heading.setAttribute(
          'aria-expanded',
          shouldOpen ? 'true' : 'false'
        );

        return;
      }

      animateMobileHeight(
        body,
        shouldOpen,
        {
          animate,

          beforeOpen() {
            segment.classList.add('is-open');

            heading.setAttribute(
              'aria-expanded',
              'true'
            );
          },

          beforeClose() {
            segment.classList.remove('is-open');

            heading.setAttribute(
              'aria-expanded',
              'false'
            );
          }
        }
      );
    }

    function closeEmphasizedSegments(
      except = null,
      animate = true
    ) {
      root
        .querySelectorAll(
          '.mr-nav-family__segment.is-open'
        )
        .forEach((segment) => {
          if (segment === except) return;

          setEmphasizedSegmentOpen(
            segment,
            false,
            animate
          );
        });
    }

    function prepareEmphasizedAccordions() {
      root
        .querySelectorAll('.mr-nav-family__links')
        .forEach((container) => {
          if (
            container.dataset.mrEmphasizedReady ===
            'true'
          ) {
            return;
          }

          container.dataset.mrEmphasizedReady =
            'true';

          const originalLinks = [
            ...container.children
          ].filter((node) =>
            node.matches('.mr-nav-link')
          );

          if (
            !originalLinks.some((node) =>
              node.matches(
                '.mr-nav-link--emphasized'
              )
            )
          ) {
            return;
          }

          let currentSegment = null;
          let currentChildren = null;

          originalLinks.forEach((link) => {
            if (
              link.matches(
                '.mr-nav-link--emphasized'
              )
            ) {
              currentSegment =
                document.createElement('div');

              currentSegment.className =
                'mr-nav-family__segment';

              currentChildren =
                document.createElement('div');

              currentChildren.className =
                'mr-nav-family__segment-children';

              container.insertBefore(
                currentSegment,
                link
              );

              currentSegment.appendChild(link);

              currentSegment.appendChild(
                currentChildren
              );
            } else if (currentChildren) {
              currentChildren.appendChild(link);
            }
          });

          container
            .querySelectorAll(
              ':scope > .mr-nav-family__segment'
            )
            .forEach((segment) => {
              const heading =
                segment.querySelector(
                  ':scope > .mr-nav-link--emphasized'
                );

              const children =
                segment.querySelector(
                  ':scope > .mr-nav-family__segment-children'
                );

              const hasChildren = Boolean(
                children &&
                children.children.length
              );

              if (!heading || !hasChildren) {
                children?.remove();
                return;
              }

              segment.classList.add(
                'mr-nav-family__segment--has-children'
              );

              heading.setAttribute(
                'aria-expanded',
                'false'
              );

              heading.setAttribute(
                'aria-haspopup',
                'true'
              );

              const viewAll =
                document.createElement('a');

              viewAll.className =
                'mr-nav-family__mobile-view-all';

              viewAll.href = heading.href;

              viewAll.dataset.mrNavDestination =
                '';

              const viewAllText =
                document.createElement('span');

              const parentCategoryName = [
                ...heading.childNodes
              ]
                .filter(
                  (node) =>
                    node.nodeType ===
                      Node.TEXT_NODE ||
                    node.nodeName === 'SPAN'
                )
                .map(
                  (node) => node.textContent
                )
                .join(' ')
                .trim();

              viewAllText.textContent =
                `${viewAllLabel} ${parentCategoryName}`;

              viewAll.appendChild(viewAllText);

              const headingIcon =
                heading.querySelector(
                  '.mr-nav-link__chevron'
                );

              if (headingIcon) {
                const viewAllIcon =
                  headingIcon.cloneNode(true);

                viewAllIcon.setAttribute(
                  'aria-hidden',
                  'true'
                );

                viewAll.appendChild(
                  viewAllIcon
                );
              }

              children.prepend(viewAll);

              heading.addEventListener(
                'click',
                (event) => {
                  const isOpen =
                    segment.classList.contains(
                      'is-open'
                    );

                  if (!isDesktop()) {
                    event.preventDefault();
                    event.stopImmediatePropagation();

                    if (isOpen) {
                      setEmphasizedSegmentOpen(
                        segment,
                        false
                      );

                      return;
                    }

                    closeEmphasizedSegments(
                      segment
                    );

                    setEmphasizedSegmentOpen(
                      segment,
                      true
                    );

                    return;
                  }

                  if (!collapseEmphasized) return;
                  if (isOpen) return;

                  event.preventDefault();
                  event.stopImmediatePropagation();

                  closeEmphasizedSegments(
                    segment,
                    false
                  );

                  setEmphasizedSegmentOpen(
                    segment,
                    true,
                    false
                  );
                }
              );
            });
        });
    }

    prepareEmphasizedAccordions();

        /* =========================================================
       NESTED DETAILS ACCORDIONS
       Cordless Tools > Saws
    ========================================================= */

    function setNestedOpen(
      nested,
      shouldOpen,
      animate = true
    ) {
      if (!nested) return;

      const body =
        nested.querySelector(
          ':scope > .mr-nav-nested__body'
        );

      if (!body) return;

      if (isDesktop()) {
        clearAnimatedStyles(body);

        nested.classList.toggle(
          'is-open',
          shouldOpen
        );

        nested.open = shouldOpen;

        return;
      }

      if (shouldOpen) {
        /*
          Native details must be open first
          so its body can be measured.
        */
        nested.open = true;

        animateMobileHeight(
          body,
          true,
          {
            animate,

            beforeOpen() {
              nested.classList.add(
                'is-open'
              );
            }
          }
        );

        return;
      }

      /*
        Keep the details element technically open
        until the closing animation completes.
      */
      animateMobileHeight(
        body,
        false,
        {
          animate,

          beforeClose() {
            nested.classList.remove(
              'is-open'
            );
          },

          afterClose() {
            nested.open = false;
          }
        }
      );
    }

    function closeNestedAccordions(
      except = null,
      animate = true
    ) {
      root
        .querySelectorAll('[data-mr-nested]')
        .forEach((nested) => {
          const isOpen =
            nested.open ||
            nested.classList.contains(
              'is-open'
            );

          if (!isOpen) return;
          if (nested === except) return;

          setNestedOpen(
            nested,
            false,
            animate
          );
        });
    }

    root
      .querySelectorAll('[data-mr-nested]')
      .forEach((nested) => {
        const summary =
          nested.querySelector(
            ':scope > summary'
          );

        const body =
          nested.querySelector(
            ':scope > .mr-nav-nested__body'
          );

        if (!summary || !body) return;

        summary.addEventListener(
          'click',
          (event) => {
            if (isDesktop()) return;

            event.preventDefault();
            event.stopImmediatePropagation();

            const isOpen =
              nested.open ||
              nested.classList.contains(
                'is-open'
              );

            if (isOpen) {
              setNestedOpen(
                nested,
                false
              );

              return;
            }

            closeNestedAccordions(
              nested
            );

            setNestedOpen(
              nested,
              true
            );
          }
        );
      });

    /* =========================================================
       DRAWER STATE
    ========================================================= */

    function resetMobileAccordionState({
      animate = false
    } = {}) {
      closeGroups(null, animate);

      closeFeatureAccordions(
        null,
        animate
      );

      closeEmphasizedSegments(
        null,
        animate
      );

      closeNestedAccordions(
        null,
        animate
      );

      closeTopDropdowns(
        null,
        animate
      );
    }

    function openMobile() {
      if (isDesktop()) return;

      previousBodyOverflow =
        document.body.style.overflow;

      document.body.style.overflow =
        'hidden';

      root.classList.add(
        'is-mobile-open'
      );

      mobileToggle?.setAttribute(
        'aria-expanded',
        'true'
      );

      window.requestAnimationFrame(() => {
        drawer
          ?.querySelector(
            'a, button, input, select'
          )
          ?.focus({
            preventScroll: true
          });
      });
    }

    function closeMobile({
      restoreFocus = false
    } = {}) {
      root.classList.remove(
        'is-mobile-open'
      );

      mobileToggle?.setAttribute(
        'aria-expanded',
        'false'
      );

      document.body.style.overflow =
        previousBodyOverflow;

      /*
        Drawer is disappearing, so reset
        everything instantly.
      */
      resetMobileAccordionState({
        animate: false
      });

      if (restoreFocus) {
        mobileToggle?.focus({
          preventScroll: true
        });
      }
    }

    mobileToggle?.addEventListener(
      'click',
      () => {
        if (
          root.classList.contains(
            'is-mobile-open'
          )
        ) {
          closeMobile();
        } else {
          openMobile();
        }
      }
    );

    mobileClose?.addEventListener(
      'click',
      () => {
        closeMobile({
          restoreFocus: true
        });
      }
    );

    /* =========================================================
       TOP LEVEL NAV EVENTS
    ========================================================= */

    topDropdowns.forEach((item) => {
      const toggle =
        item.querySelector(
          '[data-mr-top-toggle]'
        );

      if (!toggle) return;

      toggle.addEventListener(
        'click',
        (event) => {
          event.preventDefault();

          const isOpen =
            item.classList.contains(
              'is-open'
            );

          if (isOpen) {
            closeTopDropdown(item);
          } else {
            openTopDropdown(item);
          }
        }
      );
    });

    /* =========================================================
       FEATURED RANGE EVENTS
    ========================================================= */

    featureToggles.forEach((toggle) => {
      toggle.addEventListener(
        'click',
        (event) => {
          if (isDesktop()) return;

          const feature =
            toggle.closest(
              '[data-mr-feature-accordion]'
            );

          if (!feature) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          const isOpen =
            feature.classList.contains(
              'is-open'
            );

          closeFeatureAccordions(
            feature
          );

          closeGroups();

          closeEmphasizedSegments();

          closeNestedAccordions();

          setFeatureOpen(
            feature,
            !isOpen
          );
        }
      );
    });

    /* =========================================================
       STANDARD GROUP EVENTS
    ========================================================= */

    groupToggles.forEach((toggle) => {
      toggle.addEventListener(
        'click',
        (event) => {
          if (isDesktop()) return;

          const group =
            toggle.closest(
              '[data-mr-group]'
            );

          if (!group) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          const isOpen =
            group.classList.contains(
              'is-open'
            );

          closeGroups(group);

          closeEmphasizedSegments();

          closeFeatureAccordions();

          closeNestedAccordions();

          setGroupOpen(
            group,
            !isOpen
          );
        }
      );
    });

        /* =========================================================
       DESTINATION LINKS
    ========================================================= */

    root.addEventListener(
      'click',
      (event) => {
        const link =
          event.target.closest(
            '[data-mr-nav-destination]'
          );

        if (
          !link ||
          !root.contains(link)
        ) {
          return;
        }

        if (event.defaultPrevented) {
          return;
        }

        if (!isDesktop()) {
          closeMobile();
        }
      }
    );

    /* =========================================================
       SEARCH
    ========================================================= */

    root
      .querySelector(
        '.mr-global-nav__mobile-search'
      )
      ?.addEventListener(
        'submit',
        () => {
          if (!isDesktop()) {
            closeMobile();
          }
        }
      );

    /* =========================================================
       OUTSIDE CLICK
    ========================================================= */

    document.addEventListener(
      'click',
      (event) => {
        if (isDesktop()) {
          if (!root.contains(event.target)) {
            closeTopDropdowns();
          }

          return;
        }

        if (
          !root.classList.contains(
            'is-mobile-open'
          )
        ) {
          return;
        }

        if (
          drawer?.contains(event.target) ||
          mobileToggle?.contains(
            event.target
          ) ||
          mobileClose?.contains(
            event.target
          )
        ) {
          return;
        }

        closeMobile();
      }
    );

    /* =========================================================
       ESCAPE KEY
    ========================================================= */

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') {
          return;
        }

        /*
          Close deepest accordion first.
        */
        const openNested =
          root.querySelector(
            '[data-mr-nested].is-open, [data-mr-nested][open]'
          );

        if (
          !isDesktop() &&
          openNested
        ) {
          setNestedOpen(
            openNested,
            false
          );

          return;
        }

        const openSegment =
          root.querySelector(
            '.mr-nav-family__segment.is-open'
          );

        if (
          !isDesktop() &&
          openSegment
        ) {
          setEmphasizedSegmentOpen(
            openSegment,
            false
          );

          return;
        }

        const openFeature =
          root.querySelector(
            '[data-mr-feature-accordion].is-open'
          );

        if (
          !isDesktop() &&
          openFeature
        ) {
          setFeatureOpen(
            openFeature,
            false
          );

          return;
        }

        const openGroup =
          root.querySelector(
            '[data-mr-group].is-open'
          );

        if (
          !isDesktop() &&
          openGroup
        ) {
          setGroupOpen(
            openGroup,
            false
          );

          return;
        }

        const openTop =
          root.querySelector(
            '[data-mr-top-dropdown].is-open'
          );

        if (openTop) {
          const toggle =
            openTop.querySelector(
              '[data-mr-top-toggle]'
            );

          closeTopDropdown(openTop);

          toggle?.focus({
            preventScroll: true
          });

          return;
        }

        if (
          !isDesktop() &&
          root.classList.contains(
            'is-mobile-open'
          )
        ) {
          closeMobile({
            restoreFocus: true
          });
        }
      }
    );

    /* =========================================================
       WINDOW POSITION / RESIZE
    ========================================================= */

    window.addEventListener(
      'scroll',
      setPanelTop,
      {
        passive: true
      }
    );

    window.addEventListener(
      'resize',
      setPanelTop,
      {
        passive: true
      }
    );

    function resetAllAnimatedStyles() {
      root
        .querySelectorAll(
          '[data-mr-top-panel], ' +
          '[data-mr-group-body], ' +
          '.mr-nav-feature-accordion__body, ' +
          '.mr-nav-family__segment-children, ' +
          '.mr-nav-nested__body'
        )
        .forEach((body) => {
          clearAnimatedStyles(body);
        });
    }

    /* =========================================================
       MOBILE / DESKTOP MODE CHANGE
    ========================================================= */

    const modeChanged = () => {
      /*
        Reset top-level dropdowns.
      */
      topDropdowns.forEach((item) => {
        item.classList.remove(
          'is-open'
        );

        item
          .querySelector(
            '[data-mr-top-toggle]'
          )
          ?.setAttribute(
            'aria-expanded',
            'false'
          );

        item
          .querySelector(
            '[data-mr-top-panel]'
          )
          ?.setAttribute(
            'aria-hidden',
            'true'
          );
      });

      /*
        Reset standard groups.
      */
      root
        .querySelectorAll(
          '[data-mr-group].is-open'
        )
        .forEach((group) => {
          group.classList.remove(
            'is-open'
          );

          group
            .querySelector(
              '[data-mr-group-toggle]'
            )
            ?.setAttribute(
              'aria-expanded',
              'false'
            );
        });

      /*
        Reset featured ranges.
      */
      root
        .querySelectorAll(
          '[data-mr-feature-accordion].is-open'
        )
        .forEach((feature) => {
          feature.classList.remove(
            'is-open'
          );

          feature
            .querySelector(
              '[data-mr-feature-toggle]'
            )
            ?.setAttribute(
              'aria-expanded',
              'false'
            );
        });

      /*
        Reset emphasized families.
      */
      root
        .querySelectorAll(
          '.mr-nav-family__segment.is-open'
        )
        .forEach((segment) => {
          segment.classList.remove(
            'is-open'
          );

          segment
            .querySelector(
              ':scope > .mr-nav-link--emphasized'
            )
            ?.setAttribute(
              'aria-expanded',
              'false'
            );
        });

      /*
        Reset nested details.
      */
      root
        .querySelectorAll(
          '[data-mr-nested]'
        )
        .forEach((nested) => {
          nested.classList.remove(
            'is-open'
          );

          nested.open = false;
        });

      /*
        Remove all inline animation
        styles left by mobile.
      */
      resetAllAnimatedStyles();

      /*
        If we've moved to desktop,
        remove mobile drawer state.
      */
      if (
        isDesktop() &&
        root.classList.contains(
          'is-mobile-open'
        )
      ) {
        root.classList.remove(
          'is-mobile-open'
        );

        mobileToggle?.setAttribute(
          'aria-expanded',
          'false'
        );
      }

      document.body.style.overflow =
        previousBodyOverflow;

      setPanelTop();
    };

    if (
      typeof DESKTOP.addEventListener ===
      'function'
    ) {
      DESKTOP.addEventListener(
        'change',
        modeChanged
      );
    } else if (
      typeof DESKTOP.addListener ===
      'function'
    ) {
      DESKTOP.addListener(
        modeChanged
      );
    }
  }

  /* =========================================================
     BOOT
  ========================================================= */

  function boot() {
    document
      .querySelectorAll(SELECTOR)
      .forEach(init);
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }

  document.addEventListener(
    'shopify:section:load',
    boot
  );
})();